import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiUrl } from '@/env';
import { getLocalToken, getLocalRefreshToken, removeLocalToken, removeLocalRefreshToken, saveLocalToken, saveLocalRefreshToken } from '@/utils';
import router from '@/router';
import { Store } from 'vuex';
import { State } from '@/store/state';
import { commitAddNotification, commitSetLoggedIn, commitSetToken } from '@/store/main/mutations';
import { i18n } from '@/plugins/i18n';
import * as Sentry from '@sentry/vue';
import { enrichApiError } from '@/utils/apiError';

export const HEADER_TOTAL_COUNT = 'x-total-count';

export const parseTotalCount = (response: AxiosResponse): number =>
  parseInt(response.headers[HEADER_TOTAL_COUNT], 10);

let storeInstance: Store<State> | null = null;

// Break circular dependency by injecting the store from main.ts
export const setStore = (store: Store<State>) => {
  storeInstance = store;
};

// Keep this tab's in-memory token in sync when another tab refreshes it,
// so this tab doesn't keep operating on (and re-refreshing) a stale token.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (!event.key || event.newValue === null) return;
    const userEmail = storeInstance?.state?.main?.userProfile?.email;
    if (!userEmail || event.key !== `token_${userEmail}`) return;
    if (storeInstance) {
      commitSetToken(storeInstance, event.newValue);
    }
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Picked up refreshed token from another tab',
      level: 'info',
      data: { userEmail },
    });
  });

  // A tab that's been backgrounded/suspended for a while only checks token
  // freshness lazily on its next outgoing request. Checking again on wake
  // shrinks the window where it wakes up already racing a refresh another
  // tab/device performed while it was hidden.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const userEmail = storeInstance?.state?.main?.userProfile?.email;
    const token = storeInstance?.state?.main?.token || getLocalToken(userEmail);
    if (!token || !isExpiringSoon(token)) return;
    if (!refreshPromise) {
      refreshPromise = doTokenRefresh().finally(() => { refreshPromise = null; });
    }
    refreshPromise.catch(() => {
      // refresh failed; the 401 response handler (or next request) will deal with it
    });
  });
}

const apiClient: AxiosInstance = axios.create({
  baseURL: `${apiUrl}/api/v1`,
});

function isExpiringSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' && payload.exp - Date.now() / 1000 < 3600;
  } catch {
    return false;
  }
}

let refreshPromise: Promise<void> | null = null;
let lastRefreshAt: number | null = null;
// Set when a 401/403 ends the session, cleared as soon as a token is back (next login).
let sessionCleared = false;

// Exposed so callers that are about to tear down the page (e.g. the service
// worker reloading it to pick up a new deploy) can wait for an in-flight
// refresh to persist its rotated tokens first. Reloading mid-refresh would
// otherwise abandon a refresh_token that the backend already rotated,
// leaving localStorage holding the now-superseded token for the next load.
export function getPendingTokenRefresh(): Promise<void> | null {
  return refreshPromise;
}

async function refreshTokenIfStillNeeded(userEmail?: string): Promise<void> {
  // Re-check freshness: another tab may have already refreshed while we were
  // waiting for the lock, in which case there's nothing left to do here.
  const token = getLocalToken(userEmail);
  if (!token || !isExpiringSoon(token)) return;

  const refreshToken = getLocalRefreshToken(userEmail);
  if (!refreshToken) return;

  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Refreshing access token',
    level: 'info',
    data: { userEmail, msSinceLastRefresh: lastRefreshAt ? Date.now() - lastRefreshAt : null },
  });

  try {
    const response = await apiClient.post<{ access_token: string; refresh_token: string }>(
      'login/refresh-token',
      { refresh_token: refreshToken },
    );
    const { access_token, refresh_token } = response.data;
    if (storeInstance) {
      commitSetToken(storeInstance, access_token);
    }
    if (userEmail) {
      saveLocalToken(access_token, userEmail);
      saveLocalRefreshToken(refresh_token, userEmail);
    }
    lastRefreshAt = Date.now();
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Refresh succeeded',
      level: 'info',
      data: { userEmail },
    });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Refresh failed',
      level: 'warning',
      data: { userEmail, detail: axios.isAxiosError(error) ? error.response?.data?.detail : undefined },
    });
    throw error;
  }
}

async function doTokenRefresh(): Promise<void> {
  const userEmail = storeInstance?.state?.main?.userProfile?.email;
  // Serialize the actual network refresh across browser tabs so a refresh
  // token that's already been rotated by another tab is never reused.
  if (typeof navigator !== 'undefined' && navigator.locks) {
    Sentry.addBreadcrumb({ category: 'auth', message: 'Waiting for cross-tab refresh lock', level: 'info', data: { userEmail } });
    await navigator.locks.request(`token-refresh-${userEmail || 'anonymous'}`, () =>
      refreshTokenIfStillNeeded(userEmail),
    );
  } else {
    Sentry.addBreadcrumb({ category: 'auth', message: 'navigator.locks unsupported; refreshing without cross-tab lock', level: 'warning', data: { userEmail } });
    await refreshTokenIfStillNeeded(userEmail);
  }
}

const AUTH_ENDPOINTS = ['login/access-token', 'login/refresh-token', 'login/logout'];
const PUBLIC_ROUTES = ['/upload/'];

apiClient.interceptors.request.use(
  async (config) => {
    const skipRefresh = AUTH_ENDPOINTS.some((e) => config.url?.includes(e));
    const userEmail = storeInstance?.state?.main?.userProfile?.email;
    const token = storeInstance?.state?.main?.token || getLocalToken(userEmail);

    if (!skipRefresh && token && isExpiringSoon(token)) {
      if (!refreshPromise) {
        refreshPromise = doTokenRefresh().finally(() => { refreshPromise = null; });
      }
      try {
        await refreshPromise;
      } catch {
        // refresh failed; the 401 response handler will clean up the session
      }
    }

    const currentToken = storeInstance?.state?.main?.token || getLocalToken(userEmail);
    if (currentToken) {
      sessionCleared = false;
      config.headers.Authorization = `Bearer ${currentToken}`;
    } else if (sessionCleared && !skipRefresh && router.currentRoute.path.startsWith('/main')) {
      // The session just ended (e.g. an expired token got a 403) while the page still
      // had requests queued. Sending them without credentials only produces 401s.
      throw new axios.CanceledError('Session ended; request not sent');
    }

    return config;
  },
);

// Must match the detail of deps.get_current_non_administrative_user in the backend.
const ADMINISTRATIVE_ACCESS_DENIED_DETAIL = 'Administrative users do not have access to health data';

const isAdministrativeAccessDenied = (error: any) =>
  error.response?.status === 403 && error.response?.data?.detail === ADMINISTRATIVE_ACCESS_DENIED_DETAIL;

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    if (isAdministrativeAccessDenied(error)) {
      // ID_066: a blocked endpoint is not an authentication failure, the session must stay alive.
      return Promise.reject(error);
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      const isPublicRoute = PUBLIC_ROUTES.some((p) => router.currentRoute.path.startsWith(p));
      if (isPublicRoute) {
        return Promise.reject(error);
      }

      const userEmail = storeInstance?.state?.main?.userProfile?.email;

      if (error.config?.url?.includes('login/refresh-token')) {
        const detail = error.response?.data?.detail;
        const isReuse = typeof detail === 'string' && detail.toLowerCase().includes('already used');
        Sentry.captureException(error, {
          tags: { authFailureReason: isReuse ? 'refresh_token_reused' : 'refresh_401' },
          extra: {
            userEmail,
            detail,
            msSinceLastRefresh: lastRefreshAt ? Date.now() - lastRefreshAt : null,
            hasLocksSupport: typeof navigator !== 'undefined' && !!navigator.locks,
            visibilityState: typeof document !== 'undefined' ? document.visibilityState : undefined,
          },
        });
      }

      removeLocalToken(userEmail);
      removeLocalRefreshToken(userEmail);
      sessionCleared = true;
      if (storeInstance) {
        commitSetToken(storeInstance, '');
        commitSetLoggedIn(storeInstance, false);
      }

      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(() => { });
      }

      return Promise.reject(error);
    }

    // Attach `errorCode` / `messageI18n` when the backend sends a code; a no-op otherwise.
    if (error.response?.status >= 400 && error.response?.status < 500) {
      enrichApiError(error);
    }

    // TODO: For now, let other 4xx errors propagate to callers for custom handling
    // if (error.response?.status >= 400 && error.response?.status < 500) {
    //   const detail = error.response?.data?.detail;

    //   if (detail && storeInstance) {
    //     commitAddNotification(storeInstance, { content: String(detail), color: 'error' });
    //   }

    //   return Promise.reject(error);
    // }

    if (error.response?.status >= 500 && error.response?.status < 600) {
      if (storeInstance) {
        commitAddNotification(storeInstance, { content: String(i18n.t('common.serverTemporarilyUnavailable')), color: 'error' });
      }

      Sentry.captureException(error);

      return Promise.reject(error);
    }

    if (!error.response) {
      Sentry.captureException(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
